import { useTranslation } from 'react-i18next';
import { Badge } from '../../../ui/Badge/index.js';
import { Button } from '../../../ui/Button/index.js';
import type { ConfirmedThemeTitle } from '../../../../hooks/useSnsContentGenerator.js';

export interface ThemeTitleConfirmListProps {
  /** 確定済みのテーマ・タイトル一覧 */
  items: ConfirmedThemeTitle[];
  /** 行ごとの「画像・投稿文を生成」ボタンクリック時のハンドラ */
  onGenerateImage: (item: ConfirmedThemeTitle) => void;
  /** 画像生成中のアイテムid一覧 */
  generatingIds: Set<string>;
  /** 投稿文生成中のアイテムid一覧 */
  generatingCaptionIds: Set<string>;
  /** 画像生成済みのアイテムid一覧 */
  generatedIds: Set<string>;
  /** 行ごとの削除ボタンクリック時のハンドラ */
  onRemoveItem: (id: string) => void;
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business SNSコンテンツ生成の確認画面。確定したテーマ・タイトルを一覧表示し、行ごとに個別で画像・投稿文の生成を実行、または画像生成対象から削除できる
 * @graph-connects none
 */
export function ThemeTitleConfirmList({
  items,
  onGenerateImage,
  generatingIds,
  generatingCaptionIds,
  generatedIds,
  onRemoveItem,
}: ThemeTitleConfirmListProps) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return (
      <p className="text-sm text-[var(--color-text-muted)]">
        {t('snsContentGenerator.confirmEmpty')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const isGenerating = generatingIds.has(item.id) || generatingCaptionIds.has(item.id);
        return (
          <div
            key={item.id}
            className="flex items-center justify-between gap-2 rounded-lg border border-[var(--color-border)] p-3"
          >
            <div>
              <p className="text-sm font-medium text-[var(--color-text-primary)]">{item.theme}</p>
              <p className="text-sm text-[var(--color-text-secondary)]">{item.title}</p>
            </div>
            <div className="flex items-center gap-2">
              {generatedIds.has(item.id) ? (
                <Badge variant="success" size="sm">
                  {t('snsContentGenerator.imageGenerated')}
                </Badge>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onGenerateImage(item);
                  }}
                  loading={generatingIds.has(item.id)}
                >
                  {t('snsContentGenerator.generateImage')}
                </Button>
              )}
              <button
                type="button"
                onClick={() => {
                  onRemoveItem(item.id);
                }}
                disabled={isGenerating}
                aria-label={
                  isGenerating
                    ? t('snsContentGenerator.removeConfirmedItemDisabled', { theme: item.theme })
                    : t('snsContentGenerator.removeConfirmedItem', { theme: item.theme })
                }
                className="shrink-0 px-1 text-[var(--color-text-muted)] hover:text-[var(--color-error)] transition-colors disabled:opacity-40 disabled:hover:text-[var(--color-text-muted)] disabled:cursor-not-allowed"
              >
                ×
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
